// components/common/OnboardingTour/OnboardingTour.tsx
//
// Interaktivní průvodce dashboardem postavený na driver.js.
// Spouští se automaticky při prvním vstupu do dashboardu s placeným členstvím
// (localStorage flag) a provede uživatele napříč stránkami podle TOUR_PHASES.
// Kroky s advance:'click' vyžadují kliknutí na zvýrazněný prvek.
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuth } from '../../../contexts/AuthContext';
import { notify } from '../../../components/common/Toast/toastBridge';
import { TOUR_PHASES, type TourPhase, type TourStep } from './tourPhases';
import './onboarding-tour.css';

const STORAGE_KEY = 'digibrigi_onboarding_tour_v1';

/** Demo route /preview/dashboard is mapping on /dashboard to work also on demo. */
const normalizePath = (p: string) => p.replace(/^\/preview\/dashboard/, '/dashboard');


// the tour is inited only when exists the number value to the STORAGE_KEY (non-finished phase)
const getStoredPhase = (): number | 'done' => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === 'done' || !raw) return 'done';
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n < TOUR_PHASES.length ? n : 0;
};

/** Smaže stav průvodce – při další návštěvě Přehledu se spustí od začátku. */
export const initOnboardingTour = () => localStorage.setItem(STORAGE_KEY, "0");

/** Událost pro spuštění průvodce jen pro aktuální stránku (tlačítko 📖 v hlavičce). */
const PAGE_TOUR_EVENT = 'digibrigi:page-tour';

/** Spustí interaktivního průvodce pouze pro právě zobrazenou stránku dashboardu. */
export const startPageTour = () => window.dispatchEvent(new CustomEvent(PAGE_TOUR_EVENT));

const waitForElement = (selector: string, timeoutMs: number): Promise<Element | null> =>
  new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      if (Date.now() - started > timeoutMs) return resolve(null);
      window.setTimeout(tick, 200);
    };
    tick();
  });

const OnboardingTour = () => {
  const { user } = useAuth();
  const location = useLocation();

  const driverRef = useRef<Driver | null>(null);
  const internalDestroy = useRef(false); // odlišuje programové zavření od zavření uživatelem
  const runToken = useRef(0); // zneplatňuje čekající waitForElement po změně stránky

  const isEligible =
    !!user && !!user.tier?.name && user.tier.name.toLowerCase() !== 'free' && getStoredPhase() !== 'done';

  const destroyDriver = useCallback(() => {
    if (!driverRef.current) return;
    internalDestroy.current = true;
    driverRef.current.destroy();
    driverRef.current = null;
    internalDestroy.current = false;
  }, []);

  const dismissTour = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'done');
    destroyDriver();
    notify.info('Průvodce můžeš kdykoliv spustit znovu na stránce Průvodce.');
  }, [destroyDriver]);

  const startPhase = useCallback(
    (phaseIdx: number) => {
      const finishTour = () => {
        localStorage.setItem(STORAGE_KEY, 'done');
        destroyDriver();
      };

      const phase: TourPhase | undefined = TOUR_PHASES[phaseIdx];
      if (!phase) {
        finishTour();
        return;
      }
      localStorage.setItem(STORAGE_KEY, String(phaseIdx));
      if (normalizePath(window.location.pathname) !== phase.path) return; // fáze se spustí, až sem uživatel dojde

      const advancePhase = () => {
        destroyDriver();
        const nextIdx = phaseIdx + 1;
        if (nextIdx >= TOUR_PHASES.length) {
          finishTour();
          return;
        }
        localStorage.setItem(STORAGE_KEY, String(nextIdx));
        // Další fáze na stejné stránce se spustí hned; jinak počká na navigaci.
        startPhase(nextIdx);
      };

      const token = ++runToken.current;
      const firstSelector = phase.waitFor ?? phase.steps.find((s) => s.target)?.target;

      const run = (steps: TourStep[]) => {
        if (!steps.length) {
          advancePhase();
          return;
        }
        destroyDriver();

        const isClickStep = (i: number) => steps[i]?.advance === 'click';

        const d = driver({
          animate: true,
          allowClose: true,
          overlayOpacity: 0.65,
          stagePadding: 6,
          stageRadius: 12,
          popoverClass: 'digibrigi-tour-popover',
          showProgress: true,
          progressText: '{{current}} / {{total}}',
          nextBtnText: 'Další →',
          prevBtnText: '← Zpět',
          doneBtnText: 'Pokračovat',
          disableActiveInteraction: false,
          steps: steps.map((s) => ({
            element: s.target,
            popover: {
              title: s.title,
              description: s.description,
              side: s.side,
              align: s.align ?? 'center',
              showButtons: s.advance === 'click' ? ['close' as const] : undefined,
            },
          })),
          onNextClick: () => {
            const idx = d.getActiveIndex() ?? 0;
            if (idx >= steps.length - 1) {
              advancePhase();
            } else {
              d.moveNext();
            }
          },
          onPrevClick: () => d.movePrevious(),
          onCloseClick: () => dismissTour(),
          onDestroyStarted: () => dismissTour(), // Esc / klik na overlay
          onHighlightStarted: (el) => {
            const idx = d.getActiveIndex() ?? 0;
            // Kroky vyžadující kliknutí: posun dál až po kliknutí na prvek.
            if (el && isClickStep(idx)) {
              el.addEventListener(
                'click',
                () => {
                  if (idx >= steps.length - 1) advancePhase();
                  else d.moveNext();
                },
                { once: true },
              );
            }
          },
        });

        driverRef.current = d;
        d.drive();
      };

      if (!firstSelector) {
        run(phase.steps);
        return;
      }

      waitForElement(firstSelector, phase.waitMs ?? 12000).then((el) => {
        if (token !== runToken.current) return; // mezitím došlo ke změně stránky
        if (getStoredPhase() === 'done') return;
        if (normalizePath(window.location.pathname) !== phase.path) return;
        if (!el) {
          // Stránka/prvek není k dispozici – fázi přeskočíme.
          advancePhase();
          return;
        }
        // Volitelné kroky bez existujícího prvku vynecháme.
        run(phase.steps.filter((s) => !s.optional || !s.target || document.querySelector(s.target)));
      });
    },
    [destroyDriver, dismissTour],
  );

  // ── Průvodce jen pro aktuální stránku (tlačítko 📖) ────────────────
  // Poskládá kroky ze všech fází dané route, vynechá navigační kroky
  // (advance:'click', které vedou na jiné stránky) a kroky, jejichž prvek
  // na stránce právě není. Běží jako samostatný driver bez ukládání stavu.
  const runPageTour = useCallback(() => {
    destroyDriver();

    const norm = normalizePath(window.location.pathname);
    const steps = TOUR_PHASES.filter((p) => p.path === norm)
      .flatMap((p) => p.steps)
      .filter((s) => s.advance !== 'click' && (!s.target || document.querySelector(s.target)));

    if (!steps.length) {
      notify.info('Pro tuto stránku zatím není interaktivní průvodce k dispozici.');
      return;
    }

    const d = driver({
      animate: true,
      allowClose: true,
      overlayOpacity: 0.65,
      stagePadding: 6,
      stageRadius: 12,
      popoverClass: 'digibrigi-tour-popover',
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      nextBtnText: 'Další →',
      prevBtnText: '← Zpět',
      doneBtnText: 'Hotovo',
      steps: steps.map((s) => ({
        element: s.target,
        popover: { title: s.title, description: s.description, side: s.side, align: s.align ?? 'center' },
      })),
      onNextClick: () => {
        const idx = d.getActiveIndex() ?? 0;
        if (idx >= steps.length - 1) destroyDriver();
        else d.moveNext();
      },
      onPrevClick: () => d.movePrevious(),
      onCloseClick: () => destroyDriver(),
      onDestroyStarted: () => destroyDriver(),
    });

    driverRef.current = d;
    d.drive();
  }, [destroyDriver]);

  useEffect(() => {
    const handler = () => runPageTour();
    window.addEventListener(PAGE_TOUR_EVENT, handler);
    return () => window.removeEventListener(PAGE_TOUR_EVENT, handler);
  }, [runPageTour]);

  useEffect(() => {
    if (!isEligible) return;
    const stored = getStoredPhase();
    if (stored === 'done') return;

    // Aktivní driver na jiné stránce (uživatel odešel jinam) zrušíme.
    const phase = TOUR_PHASES[stored];
    if (driverRef.current && phase && normalizePath(location.pathname) !== phase.path) destroyDriver();
    if (!driverRef.current) startPhase(stored);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isEligible]);

  // Úklid při odmountování layoutu (odhlášení apod.).
  useEffect(() => () => destroyDriver(), [destroyDriver]);

  return null;
};

export default OnboardingTour;
